import { MAX_DISSOLVE_ROWS } from "./dissolve";
import { ROWS_PER_ROPE_INCH } from "./knotCalc";

export { ROWS_PER_ROPE_INCH };

/**
 * Total chart rows for a finished rope length.
 * One inch of rope is 16 rows, at any beads-around.
 */
export function rowsForLengthInches(lengthInches) {
  const inches = Number(lengthInches);
  if (!(inches > 0)) return 0;
  const rows = Math.round(inches * ROWS_PER_ROPE_INCH);
  if (rows < 1) return 0;
  return Math.min(MAX_DISSOLVE_ROWS, rows);
}

/**
 * Offset-chart row cycle. The rope view's bead placement repeats every
 * `2 * cols + 1` rows; other heights shear the same colors.
 */
export function offsetRowCycle(cols) {
  const width = Math.max(1, Math.floor(Number(cols)) || 1);
  return 2 * width + 1;
}

/**
 * Nearest row count to `targetRows` that keeps the offset view in phase
 * with `currentRows`. The rope view repeats every `2 * cols + 1` rows;
 * other heights shear the same colors.
 */
export function rowsPreservingOffsetPhase(targetRows, currentRows, cols) {
  const cycle = offsetRowCycle(cols);
  const current = Math.max(0, Math.floor(Number(currentRows)) || 0);
  const target = Math.max(0, Math.floor(Number(targetRows)) || 0);
  if (target < 1 || current < 1 || cycle <= 1) return target;

  const phase = current % cycle;
  const start = phase === 0 ? cycle : phase;
  let best = start;
  for (let n = start; n <= target + cycle; n += cycle) {
    const better =
      Math.abs(n - target) < Math.abs(best - target) ||
      (Math.abs(n - target) === Math.abs(best - target) && n < best);
    if (better) best = n;
  }
  return best;
}

/** Finished rope inches implied by the current row count. */
export function inchesFromRows(rowCount) {
  const rows = Math.max(0, Math.floor(Number(rowCount)) || 0);
  if (rows < 1) return 0;
  return rows / ROWS_PER_ROPE_INCH;
}

function chartBeads(pattern, cols, rows, defaultColor) {
  const total = cols * rows;
  return Array.from({ length: total }, (_, index) => {
    const value = Array.isArray(pattern) ? pattern[index] : undefined;
    return value == null || value === "" ? defaultColor : value;
  });
}

/**
 * Resize the chart so its total height matches `inches`. Grows by empty rows
 * under the pattern (low indices); shrinks by dropping rows from the bottom.
 */
export function resizePatternToInches({
  pattern,
  cols,
  rows,
  inches,
  defaultColor,
}) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const r = Math.max(0, Math.floor(Number(rows)) || 0);
  const targetRows = rowsPreservingOffsetPhase(
    rowsForLengthInches(inches),
    r,
    c
  );
  const source = chartBeads(pattern, c, r, defaultColor);

  if (targetRows < 1) {
    return {
      pattern: source,
      cols: c,
      rows: r,
      targetRows: r,
      addedRows: 0,
      removedRows: 0,
    };
  }

  if (targetRows === r) {
    return {
      pattern: source,
      cols: c,
      rows: r,
      targetRows,
      addedRows: 0,
      removedRows: 0,
    };
  }

  if (targetRows > r) {
    const addedRows = targetRows - r;
    const blank = Array(c * addedRows).fill(defaultColor);
    return {
      pattern: blank.concat(source),
      cols: c,
      rows: targetRows,
      targetRows,
      addedRows,
      removedRows: 0,
    };
  }

  const removedRows = r - targetRows;
  return {
    pattern: source.slice(c * removedRows),
    cols: c,
    rows: targetRows,
    targetRows,
    addedRows: 0,
    removedRows,
  };
}
